import { useEffect, useMemo, useState } from "react";
import { MessageCircle, CheckCircle2, CalendarDays } from "lucide-react";
import { format, startOfDay } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { buildWaMessage, waUrl } from "@/lib/api";
import { scheduleFor, slotsFor, dayHasHours } from "@/lib/schedule";

const EMPTY = { name: "", area: "", service: "", date: null, time: "", homecare: "tidak", question: "" };
const REQUIRED = { name: "Nama wajib diisi.", area: "Alamat / area wajib diisi.", service: "Pilih layanan yang dibutuhkan.", date: "Pilih tanggal yang diinginkan.", time: "Pilih jam yang diinginkan." };
export const CONFIRM_NOTE = "Jadwal yang dipilih merupakan permintaan awal dan akan dikonfirmasi oleh admin Nanina Care.";

const inputCls = "mt-1.5 w-full rounded-xl border border-[#DCEAE6] bg-[#FAFEFF] px-3 py-2.5 text-sm text-nanina-ink outline-none focus:border-nanina-sky focus:ring-2 focus:ring-nanina-sky/40";

const Field = ({ label, error, children, wide, id }) => (
  <label className={`block text-xs font-semibold text-nanina-green ${wide ? "sm:col-span-2" : ""}`} htmlFor={id}>
    {label}
    {children}
    {error && <span className="mt-1 block text-[11px] font-medium text-red-600" data-testid={`inquiry-error-${id}`}>{error}</span>}
  </label>
);

const useServiceOptions = (site) => useMemo(() => {
  const opts = site.categories.flatMap((c) => [
    { label: c.name, catId: c.id },
    ...site.treatments.filter((t) => t.category_id === c.id).map((t) => ({ label: `${c.name} / ${t.name}`, catId: c.id })),
  ]);
  return opts.concat((site.packages || []).map((p) => ({ label: `Paket: ${p.name}`, catId: p.category_ids?.[0] || "" })));
}, [site]);

const DateField = ({ value, onChange, sched, error }) => {
  const [open, setOpen] = useState(false);
  const today = startOfDay(new Date());
  const disabled = (d) => d < today || (sched.rows.length > 0 && !dayHasHours(sched, d));
  return (
    <Field label="Tanggal yang Diinginkan *" error={error} id="date">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" id="date" className={`${inputCls} flex items-center gap-2 text-left ${value ? "" : "text-nanina-muted"}`} data-testid="inquiry-date-button">
            <CalendarDays className="h-4 w-4 shrink-0 text-nanina-green" />
            {value ? format(value, "EEEE, d MMMM yyyy", { locale: idLocale }) : "Pilih tanggal"}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 rounded-2xl" align="start" data-testid="inquiry-calendar">
          <Calendar mode="single" locale={idLocale} selected={value || undefined} defaultMonth={value || today} fromDate={today} disabled={disabled}
            onSelect={(d) => { onChange(d || null); setOpen(false); }} initialFocus />
        </PopoverContent>
      </Popover>
    </Field>
  );
};

const TimeField = ({ form, sched, onChange, error }) => {
  if (!sched.rows.length) {
    return (
      <Field label="Jam yang Diinginkan (permintaan) *" error={error} id="time">
        <input id="time" className={inputCls} value={form.time} onChange={(e) => onChange(e.target.value)} placeholder="Contoh: pagi, sekitar 09.00" data-testid="inquiry-time-input" />
        <span className="mt-1 block text-[11px] font-normal text-nanina-muted" data-testid="inquiry-no-schedule">Hubungi admin untuk informasi jadwal. Jam yang Anda tulis bersifat permintaan dan belum dikonfirmasi.</span>
      </Field>
    );
  }
  const slots = slotsFor(sched, form.date);
  return (
    <Field label="Jam yang Diinginkan (permintaan) *" error={error} id="time">
      <select id="time" className={inputCls} value={form.time} onChange={(e) => onChange(e.target.value)} disabled={!form.date || !slots.length} data-testid="inquiry-time-select">
        <option value="">{!form.date ? "Pilih tanggal terlebih dahulu" : slots.length ? "Pilih jam" : "Tidak ada jam tersedia"}</option>
        {slots.map((s) => <option key={s} value={s}>{s.replace(":", ".")} WIB</option>)}
      </select>
      {form.date && !slots.length && <span className="mt-1 block text-[11px] font-normal text-nanina-muted" data-testid="inquiry-no-slots">Tidak ada jam layanan pada tanggal ini. Silakan pilih tanggal lain.</span>}
    </Field>
  );
};

export const InquiryDialog = ({ state, onClose, site }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const options = useServiceOptions(site);

  useEffect(() => {
    if (state.open) {
      setForm({ ...EMPTY, service: state.service || "", homecare: state.homecare || "tidak" });
      setErrors({});
      setSent(false);
    }
  }, [state]);

  const opts = form.service && !options.some((o) => o.label === form.service) ? [{ label: form.service, catId: "" }, ...options] : options;
  const catId = opts.find((o) => o.label === form.service)?.catId;
  const sched = scheduleFor(site.settings, catId);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  useEffect(() => {
    setErrors((prev) => {
      const next = Object.fromEntries(Object.entries(prev).filter(([k]) => !form[k]));
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [form]);

  const message = () => buildWaMessage({ ...form, dateText: form.date ? format(form.date, "EEEE, d MMMM yyyy", { locale: idLocale }) : "" });

  const submit = (e) => {
    e.preventDefault();
    const errs = Object.fromEntries(Object.entries(REQUIRED).filter(([k]) => (k === "date" ? !form.date : !String(form[k]).trim())));
    setErrors(errs);
    if (Object.keys(errs).length) return;
    window.open(waUrl(site.settings.whatsapp_number, message()), "_blank", "noopener,noreferrer");
    setSent(true);
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto rounded-3xl bg-white p-5 sm:p-7" data-testid="inquiry-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="text-xl sm:text-2xl font-extrabold tracking-tight text-nanina-green">Formulir Pertanyaan</DialogTitle>
          <DialogDescription className="text-xs text-nanina-muted">Lengkapi data singkat berikut. Setelah dikirim, WhatsApp akan terbuka dengan pesan yang sudah tersusun.</DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="py-6 text-center" data-testid="inquiry-success">
            <CheckCircle2 className="h-10 w-10 mx-auto text-nanina-fresh" />
            <p className="mt-3 font-semibold text-nanina-green">WhatsApp sedang dibuka</p>
            <p className="mt-1 text-xs text-nanina-muted">Silakan tekan kirim di WhatsApp. Admin akan membalas dan mengonfirmasi jadwal. Pesan ini belum merupakan booking.</p>
            <a href={waUrl(site.settings.whatsapp_number, message())} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block text-xs font-semibold text-nanina-green underline" data-testid="inquiry-open-again-link">WhatsApp tidak terbuka? Klik di sini</a>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="grid sm:grid-cols-2 gap-3 mt-1" data-testid="inquiry-form">
            <Field label="Nama *" error={errors.name} id="name"><input id="name" className={inputCls} value={form.name} onChange={set("name")} autoComplete="name" data-testid="inquiry-name-input" /></Field>
            <Field label="Alamat / Area *" error={errors.area} id="area"><input id="area" className={inputCls} value={form.area} onChange={set("area")} placeholder="Contoh: Umbulharjo, Yogyakarta" data-testid="inquiry-area-input" /></Field>
            <Field label="Layanan *" error={errors.service} id="service" wide>
              <select id="service" className={inputCls} value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value, date: null, time: "" })} data-testid="inquiry-service-select">
                <option value="">Pilih layanan</option>
                {opts.map((o) => <option key={o.label} value={o.label}>{o.label}</option>)}
              </select>
            </Field>
            <DateField value={form.date} sched={sched} error={errors.date} onChange={(d) => setForm({ ...form, date: d, time: "" })} />
            <TimeField form={form} sched={sched} error={errors.time} onChange={(t) => setForm({ ...form, time: t })} />
            <p className="sm:col-span-2 rounded-xl bg-nanina-skysoft/60 px-3 py-2 text-[11px] text-nanina-green" data-testid="inquiry-schedule-note">{CONFIRM_NOTE}</p>
            <fieldset className="sm:col-span-2">
              <legend className="text-xs font-semibold text-nanina-green">Homecare (kunjungan ke rumah)?</legend>
              <div className="mt-1.5 flex gap-2" role="radiogroup">
                {["ya", "tidak"].map((v) => (
                  <button type="button" key={v} role="radio" aria-checked={form.homecare === v} onClick={() => setForm({ ...form, homecare: v })} className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${form.homecare === v ? "bg-nanina-green text-white border-nanina-green" : "bg-white text-nanina-green border-[#DCEAE6]"}`} data-testid={`inquiry-homecare-${v}`}>
                    {v === "ya" ? "Ya" : "Tidak"}
                  </button>
                ))}
              </div>
              {form.homecare === "ya" && <p className="mt-1.5 text-[11px] text-nanina-muted">Maksimal 15 km dari lokasi Nanina Care/PMB Nina. Ongkos perjalanan dikonfirmasi admin.</p>}
            </fieldset>
            <Field label="Pertanyaan Tambahan (opsional)" id="question" wide>
              <textarea id="question" rows={3} className={inputCls} value={form.question} onChange={set("question")} placeholder="Tuliskan pertanyaan singkat. Mohon tidak menuliskan riwayat medis detail." data-testid="inquiry-question-input" />
            </Field>
            <button type="submit" className="sm:col-span-2 mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-nanina-green text-white text-sm font-semibold px-5 py-3.5 hover:bg-nanina-greendark transition-colors" data-testid="inquiry-submit-button">
              <MessageCircle className="h-4 w-4" /> Lanjut ke WhatsApp
            </button>
            <p className="sm:col-span-2 text-[11px] text-nanina-muted" data-testid="inquiry-privacy-note">Privasi: informasi yang Anda isi hanya disiapkan menjadi pesan WhatsApp untuk admin Nanina Care dan tidak disimpan di website. Pengiriman formulir bukan konfirmasi booking.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
